import React, { useRef, useState } from 'react'
import { Box } from '@chakra-ui/core'
import { css, keyframes } from '@emotion/core'
import { useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'

export type StepDirection = 'forward' | 'backward'

const slideFromPositive = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
`

const slideFromNegative = keyframes`
  from { transform: translateX(-100%); opacity: 0; }
  to   { transform: translateX(0);     opacity: 1; }
`

/**
 * Tracks which way a stepped flow is moving, from a monotonically increasing depth (a step-stack
 * length, or a hand-assigned index where there is no stack).
 *
 * Must be called by a component that does NOT remount between steps — usually the step dispatcher —
 * because the previous depth lives in a ref. Direction is resolved during render, not in an effect, so
 * the panel animates on the very first frame of the new step instead of one frame late.
 *
 * `hasNavigated` stays false until the first change, which is how the initial step avoids sliding in on
 * top of the modal's own opening transition.
 */
export const useStepDirection = (depth: number): { direction: StepDirection; hasNavigated: boolean } => {
  const prevDepthRef = useRef(depth)
  const directionRef = useRef<StepDirection>('forward')
  const hasNavigatedRef = useRef(false)

  if (depth !== prevDepthRef.current) {
    directionRef.current = depth > prevDepthRef.current ? 'forward' : 'backward'
    prevDepthRef.current = depth
    hasNavigatedRef.current = true
  }

  return { direction: directionRef.current, hasNavigated: hasNavigatedRef.current }
}

interface IProps {
  children: React.ReactNode
  /** Which edge the content comes from: forward → trailing, backward → leading. Mirrored under RTL. */
  direction?: StepDirection
  /** Render children untouched — no animation, no clipping. Used for the first step of a flow. */
  isEnabled?: boolean
  /**
   * Set when the panel must be allowed to shrink below its content and scroll internally instead — the
   * full-page `SubtaskModal` card, which owns its own inner scroller. Adds `min-height: 0`.
   */
  grow?: boolean
}

/**
 * Slides its content in when a step changes: from the trailing edge going forward, from the leading edge
 * going back (right/left in LTR, mirrored in RTL).
 *
 * Task creation keeps every step in the *same* modal, so without the motion a step change reads as the
 * content silently swapping and the user gets no sense of moving through a flow — least of all when
 * entering the AI sub-flow or the GPT conversation, which are conceptually somewhere else entirely.
 * Remount it with a `key` per step to replay the animation.
 *
 * The clipping is temporary on purpose. The modal body is an `overflowY: auto` scroller, so an element
 * translated 100% to the side would give it a horizontal scrollbar for the duration of the animation —
 * hence `overflow: hidden` while animating, released on `animationend` so nothing inside (popovers,
 * dropdowns, lightboxes) stays clipped afterwards.
 */
export const SlideInPanel: React.FC<IProps> = ({ children, direction = 'forward', isEnabled = true, grow = false }) => {
  const isRtl = useSelector(isRtlSelector)

  // Under reduced motion there is no animation, so `animationend` never fires — start unclipped or the
  // content would stay clipped forever.
  const [isAnimating, setIsAnimating] = useState(
    () => isEnabled && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  )

  /**
   * The panel becomes a flex item of whatever it is dropped into, so it has to be layout-transparent.
   *
   * In the modal body — a fixed-height `flexDir="column"` scroller with `justifyContent="space-between"`
   * — two things must hold or the body breaks:
   *
   * - `flexShrink: 0`. The default `1` shrinks the panel to the container height, so the container's
   *   scrollHeight equals its own height: nothing scrolls at all, and `useScrollMemory` has nowhere to
   *   restore `scrollTop` to. This is exactly how the scroll memory regressed once every step (not just
   *   the AI ones) got wrapped.
   * - `flexGrow: 1` plus the same column/space-between distribution. Steps used to hand the body several
   *   children — step 1 gives it the subtask list AND the validation message, pinned to the bottom — and
   *   now hand it one. Reproducing the distribution inside keeps that layout identical. With a single
   *   child space-between is a no-op, so it is safe everywhere else.
   */
  const layoutProps = grow
    ? // The page-flow card owns an inner `overflowY: auto` scroller, so here the panel must be
      // constrained to the card and allowed to shrink — the exact opposite of the modal body.
      ({ display: 'flex', flexDirection: 'column', flex: '1', minH: 0 } as const)
    : ({
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        flexGrow: 1,
        flexShrink: 0,
      } as const)

  if (!isEnabled) return <>{children}</>

  // Forward means "away from the reading start": right in LTR, left in RTL. Back is the opposite.
  const fromPositive = (direction === 'forward') !== isRtl

  return (
    <Box w="100%" overflow={isAnimating ? 'hidden' : 'visible'} {...layoutProps}>
      <Box
        {...layoutProps}
        onAnimationEnd={() => setIsAnimating(false)}
        css={css`
          animation: ${fromPositive ? slideFromPositive : slideFromNegative} 280ms cubic-bezier(0.22, 1, 0.36, 1) both;
          will-change: transform, opacity;

          @media (prefers-reduced-motion: reduce) {
            animation: none;
          }
        `}
      >
        {children}
      </Box>
    </Box>
  )
}
